import { ref, computed, watch, onMounted } from "vue";
import { useConversation } from "../../../Stores/Conversation";
import { useAppState } from "../../../Stores/AppState";
import { useSettings } from "../../Settings/Store/Settings";
import { useRoute } from "vue-router";
import { ROUTE_NAMES } from "../../../Constants/routes";
import utilities from "../../../Helpers/utilities";
import { useUnlinkedPanelState } from "../../../Stores/useUnlinkedPanelState";
import { useAlignStore } from "../../../Stores/Align";
import { MEET_YOUR_KAAMFU_AI } from "../../../Constants/menus";

export function useWindowPopoverState() {
    // Stores
    const conversation_store = useConversation();
    const app_state_store = useAppState();
    const settings_store = useSettings();
    const route = useRoute();
    const { chatPopover, toggleChatPopover } = utilities();
    const unlinkedPanelState = useUnlinkedPanelState();
    const alignStore = useAlignStore();
    // Core state
    const is_hidden = ref(true);
    const is_fade_in_started = ref(true);
    const show_bread_crumbs = ref(false);

    // Computed properties
    const active_conversation = computed(() => conversation_store.active);
    const isSettingsRoute = computed(() => route.name === ROUTE_NAMES.SETTINGS);

    const is_team_opened = computed(
        () => app_state_store.invite_drawer?.is_team_opened,
    );

    const is_workday_opened = computed(
        () =>
            app_state_store.invite_drawer?.is_workday_opened &&
            isSettingsRoute.value &&
            settings_store.is_workday_tab_opened &&
            chatPopover.value,
    );

    const is_workday_permission_opened = computed(
        () =>
            app_state_store.invite_drawer?.is_workday_permission_opened &&
            isSettingsRoute.value &&
            settings_store.is_workday_tab_opened,
    );

    const is_manage_organization_role_opened = computed(
        () => app_state_store.is_manage_organization_role_opened,
    );

    const is_productivity_opened = computed(
        () => app_state_store.productivity_drawer?.is_productivity_opened,
    );

    const isChatOpened = computed(() => {
        return (
            Boolean(active_conversation.value) &&
            !is_team_opened.value &&
            !is_productivity_opened.value &&
            chatPopover.value
        );
    });

    const is_conversation_hidden = computed(() => {
        return is_hidden.value && !is_team_opened.value;
    });

    // Initialize component state
    const initializeState = () => {
        if (!active_conversation.value || active_conversation.value.isFake()) {
            is_hidden.value = true;
        } else {
            is_hidden.value = false;
            is_fade_in_started.value = false;
        }
    };

    // Handle conversation changes
    const handleConversationChange = (value) => {
        // Reset workday states
        conversation_store.toggleGetHelpNowMenuValue(false);
        app_state_store.invite_drawer.is_workday_permission_opened = false;
        app_state_store.invite_drawer.is_workday_opened = false;

        // Update visibility states
        is_hidden.value = false;
        is_fade_in_started.value = true;

        // Delayed state updates
        timeOutHelper(() => {
            is_hidden.value = false;
        }, 300);

        timeOutHelper(() => {
            is_fade_in_started.value = false;
        }, 600);
    };

    // Breadcrumb management
    const bread_crumb_pending_hide = ref(false);

    const activateBreadCrumbs = (activate = true) => {
        if (is_team_opened.value) return;

        if (!activate) {
            bread_crumb_pending_hide.value = true;
            timeOutHelper(() => {
                if (bread_crumb_pending_hide.value) {
                    show_bread_crumbs.value = false;
                }
            }, 1);
        } else {
            bread_crumb_pending_hide.value = false;
            show_bread_crumbs.value = true;
        }
    };

    // Watchers
    watch(active_conversation, handleConversationChange);

    watch(
        [is_team_opened, is_workday_opened, is_productivity_opened],
        ([valA, valB, valC]) => {
            if (valA || valB || valC) {
                conversation_store.toggleGetHelpNowMenuValue(false);
            }
        },
    );

    const onClose = () => {
        if (is_productivity_opened.value || is_team_opened.value) {
            if (app_state_store.show_control_panel) return;
            unlinkedPanelState.restrictRecoveryMode();
            app_state_store.invite_drawer.is_workday_opened = false;
            app_state_store.invite_drawer.is_workday_permission_opened = false;

            if (is_productivity_opened.value) {
                conversation_store.toggleTeamPanel(
                    false,
                    conversation_store.active,
                );
                conversation_store.toggleProductivityPanel(false);
                unlinkedPanelState.openProductivityPanelTracked(false);
            }
            if (is_team_opened.value) {
                conversation_store.toggleProductivityPanel(false);
                conversation_store.toggleTeamPanel(
                    Boolean(unlinkedPanelState.savedRoutes.directory?.name),
                    conversation_store.active,
                );
                unlinkedPanelState.openDirectoryPanelTracked(false);
            }
        } else {
            const state = conversation_store.active?.state ?? {};
            app_state_store.toggleInviteDrawer(false, state, false);
            conversation_store.toggleProductivityPanel(false);

            if (
                conversation_store.is_get_help_now_opened &&
                chatPopover.value
            ) {
                conversation_store.toggleGetHelpNowMenuValue(false);
                return;
            }

            toggleChatPopover(
                null,
                true,
                Boolean(unlinkedPanelState.savedRoutes.chat?.name),
            );

            unlinkedPanelState.switchToLastDetachChatRouteFromUnlinkedPanel();
            app_state_store.toggleInviteDrawer(false, state);

            if (
                conversation_store.active?.isAgentConversation() &&
                alignStore.selectedStep?.type === MEET_YOUR_KAAMFU_AI
            ) {
                alignStore.setSelectedStep(null);
            }
        }
    };

    onMounted(() => {
        timeOutHelper(initializeState, 100);
    });

    return {
        // State
        is_hidden,
        is_fade_in_started,
        show_bread_crumbs,

        // Computed
        active_conversation,
        is_team_opened,
        is_workday_opened,
        is_workday_permission_opened,
        is_manage_organization_role_opened,
        is_productivity_opened,
        is_conversation_hidden,
        isChatOpened,
        // Methods
        initializeState,
        activateBreadCrumbs,
        onClose,
    };
}
